import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import { formatDate } from '../utils/formatDateTime';
import {
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const EMPTY_FORM = {
  name: '',
  code: '',
  contactPerson: '',
  email: '',
  phone: '',
  address: '',
  contractStart: '',
  contractEnd: '',
  insuranceExpiry: '',
};

function ContractorFormModal({ isOpen, onClose, onSubmit, initial, isSubmitting }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(
        initial
          ? {
              name: initial.name || '',
              code: initial.code || '',
              contactPerson: initial.contactPerson || '',
              email: initial.email || '',
              phone: initial.phone || '',
              address: initial.address || '',
              contractStart: initial.contractStart ? initial.contractStart.split('T')[0] : '',
              contractEnd: initial.contractEnd ? initial.contractEnd.split('T')[0] : '',
              insuranceExpiry: initial.insuranceExpiry ? initial.insuranceExpiry.split('T')[0] : '',
            }
          : EMPTY_FORM
      );
      setErrors({});
    }
  }, [isOpen, initial]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.code.trim()) e.code = 'Required';
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      contactPerson: form.contactPerson.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      contractStart: form.contractStart || null,
      contractEnd: form.contractEnd || null,
      insuranceExpiry: form.insuranceExpiry || null,
    });
  };

  if (!isOpen) return null;

  const field = (label, key, props = {}) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label}{props.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={props.type || 'text'}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={props.placeholder}
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-500 ${
          errors[key] ? 'border-red-400 bg-red-50' : 'border-slate-300'
        }`}
      />
      {errors[key] && <p className="mt-1 text-xs text-red-500">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl z-10 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">
              {initial ? 'Edit Contractor' : 'Add Contractor Company'}
            </h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
            <div className="px-6 py-5 space-y-5">
              {/* Company Info */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Company Information
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {field('Company Name', 'name', { required: true, placeholder: 'ABC Construction Ltd.' })}
                  {field('Code', 'code', { required: true, placeholder: 'e.g. ABC-CON' })}
                  {field('Contact Person', 'contactPerson', { placeholder: 'John Smith' })}
                  {field('Email', 'email', { type: 'email', placeholder: 'contact@company.com' })}
                  {field('Phone', 'phone', { type: 'tel', placeholder: '+91 98765 43210' })}
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <textarea
                    value={form.address}
                    onChange={(e) => set('address', e.target.value)}
                    placeholder="Full address..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>
              </div>

              {/* Contract Dates */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Contract Details
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {field('Contract Start', 'contractStart', { type: 'date' })}
                  {field('Contract End', 'contractEnd', { type: 'date' })}
                  {field('Insurance Expiry', 'insuranceExpiry', { type: 'date' })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 rounded-b-xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {isSubmitting ? 'Saving...' : initial ? 'Save Changes' : 'Add Contractor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function insuranceStatus(expiry) {
  if (!expiry) return null;
  const days = Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: 'Expired', cls: 'bg-red-100 text-red-700' };
  if (days <= 30) return { label: `Expires in ${days}d`, cls: 'bg-amber-100 text-amber-700' };
  return { label: 'Valid', cls: 'bg-emerald-100 text-emerald-700' };
}

export default function ContractorsPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [deactivating, setDeactivating] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever the filters that scope the result set change.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilter]);

  const queryParams = {
    search: debouncedSearch || undefined,
    isActive: activeFilter !== 'all' ? activeFilter : undefined,
    page,
    limit: PAGE_SIZE,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['contractors', queryParams],
    queryFn: () => contractorsAPI.getAll(queryParams).then((r) => r.data),
  });

  const contractors = data?.data || [];
  const total = data?.pagination?.total || 0;
  const pages = data?.pagination?.pages || 1;

  const createMutation = useMutation({
    mutationFn: (payload) => contractorsAPI.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractors'] });
      toast.success('Contractor added successfully');
      setShowForm(false);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to add contractor'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => contractorsAPI.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractors'] });
      toast.success('Contractor updated');
      setShowForm(false);
      setEditingContractor(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to update contractor'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id) => contractorsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractors'] });
      toast.success('Contractor deactivated');
      setDeactivating(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to deactivate contractor'),
  });

  const handleSubmit = (payload) => {
    if (editingContractor) {
      updateMutation.mutate({ id: editingContractor.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const FILTER_TABS = [
    { label: 'Active', value: 'true' },
    { label: 'All', value: 'all' },
    { label: 'Inactive', value: 'false' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Contractors</h1>
          <p className="text-sm text-slate-500 mt-1">Manage contractor companies and their details</p>
        </div>
        <button
          onClick={() => { setEditingContractor(null); setShowForm(true); }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium w-fit transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Contractor
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">{total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">
                {contractors.filter((c) => c.isActive).length}
              </p>
              <p className="text-xs text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">
                {contractors.reduce((sum, c) => sum + (c._count?.workers || 0), 0)}
              </p>
              <p className="text-xs text-slate-500">Workers</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">
                {contractors.filter((c) => c.insuranceExpiry && insuranceStatus(c.insuranceExpiry)?.label !== 'Valid').length}
              </p>
              <p className="text-xs text-slate-500">Insurance Issues</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="glass p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, code, contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveFilter(tab.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeFilter === tab.value
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {isFetching && !isLoading && <span className="text-xs text-slate-400">Updating...</span>}
      </div>

      {/* Table */}
      <div className={`glass p-0 overflow-hidden transition-opacity duration-200 ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Company</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Code</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Contact</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Workers</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Safety Rating</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Contract Period</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Insurance</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-400">Loading contractors...</td>
                </tr>
              ) : contractors.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <BuildingOfficeIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      {search ? 'No contractors match your search' : 'No contractors yet — add your first contractor'}
                    </p>
                  </td>
                </tr>
              ) : (
                contractors.map((c) => {
                  const insurance = insuranceStatus(c.insuranceExpiry);
                  return (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      {/* Company */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                            <BuildingOfficeIcon className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{c.name}</p>
                            {c.address && <p className="text-xs text-slate-400 truncate max-w-[180px]">{c.address}</p>}
                          </div>
                        </div>
                      </td>

                      {/* Code */}
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-mono">
                          {c.code}
                        </span>
                      </td>

                      {/* Contact */}
                      <td className="py-3 px-4">
                        <div className="space-y-0.5">
                          {c.contactPerson && (
                            <p className="text-sm text-slate-700">{c.contactPerson}</p>
                          )}
                          {c.email && (
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <EnvelopeIcon className="w-3 h-3" />{c.email}
                            </p>
                          )}
                          {c.phone && (
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <PhoneIcon className="w-3 h-3" />{c.phone}
                            </p>
                          )}
                          {!c.contactPerson && !c.email && !c.phone && (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </div>
                      </td>

                      {/* Workers */}
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700">
                          <UserGroupIcon className="w-3.5 h-3.5 text-slate-400" />
                          {c._count?.workers ?? c.totalWorkers}
                        </span>
                      </td>

                      {/* Safety Rating */}
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          c.safetyRating >= 80 ? 'bg-emerald-100 text-emerald-700' :
                          c.safetyRating >= 60 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {c.safetyRating}
                        </span>
                      </td>

                      {/* Contract Period */}
                      <td className="py-3 px-4">
                        {c.contractStart || c.contractEnd ? (
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <CalendarDaysIcon className="w-3 h-3" />
                            {c.contractStart ? formatDate(c.contractStart) : '—'}
                            {' → '}
                            {c.contractEnd ? formatDate(c.contractEnd) : '—'}
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Insurance */}
                      <td className="py-3 px-4 text-center">
                        {insurance ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${insurance.cls}`}>
                            {insurance.label}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 text-center">
                        {c.isActive ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">Inactive</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setEditingContractor(c); setShowForm(true); }}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          {c.isActive && (
                            <button
                              onClick={() => setDeactivating(c)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Deactivate"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!isLoading && contractors.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-400">
              Page {page} of {pages} · {total} contractor{total === 1 ? '' : 's'}
            </span>
            {pages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Go to previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Go to next page"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ContractorFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingContractor(null); }}
        onSubmit={handleSubmit}
        initial={editingContractor}
        isSubmitting={isSubmitting}
      />

      <ConfirmationModal
        isOpen={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={() => deactivateMutation.mutate(deactivating.id)}
        title="Deactivate Contractor"
        message={`Are you sure you want to deactivate "${deactivating?.name}"? Workers linked to this contractor will remain but the company will no longer appear in dropdowns.`}
        confirmLabel={deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
        variant="danger"
      />
    </div>
  );
}
